import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import { Invoice, InvoiceSearchCommand, S3Object } from '../../models/invoice/invoice';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class InvoiceProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/invoice-order';

  getInvoicesById(id: string): Observable<ApiResponse<Invoice>> {
    return this.httpClient.get<ApiResponse<Invoice>>(`${this.baseUrl}/${id}`);
  }

  getInvoicesPurchaseOrderNumber(purchaseordenumber: string): Observable<ApiResponse<Invoice>> {
    return this.httpClient.get<ApiResponse<Invoice>>(`${this.baseUrl}/purchase-order-number/${encodeURIComponent(purchaseordenumber)}`);
  }

  public getInvoices(): Observable<ApiResponse<Invoice[]>> {
    return this.httpClient.get<ApiResponse<Invoice[]>>(`${this.baseUrl}/all`);
  }

  public getInvoice(id: string): Observable<ApiResponse<Invoice>> {
    return this.httpClient.get<ApiResponse<Invoice>>(`${this.baseUrl}/${id}`);
  }

  public createInvoice(data: Invoice): Observable<ApiResponse<Invoice>> {
    return this.httpClient.post<ApiResponse<Invoice>>(this.baseUrl, data);
  }

  public updateInvoice(data: Invoice): Observable<ApiResponse<Invoice>> {
    return this.httpClient.put<ApiResponse<Invoice>>(`${this.baseUrl}/${data.id}`, data);
  }

  public searchInvoice(
    invoiceSearchCommand: InvoiceSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    matchAny = false,
    sortField?: string,
    sortOrder?: number,
  ): Observable<ApiResponse<PaginatedApiResponse<Invoice>>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('matchAny', matchAny.toString());
    if (sortField) params = params.set('sortField', sortField);
    if (sortOrder !== undefined) params = params.set('sortOrder', sortOrder.toString());
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Invoice>>>(`${this.baseUrl}/search`, invoiceSearchCommand, { params });
  }

  public uploadFiles(files: File[], invoiceID: string): Observable<S3Object[]> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append(`files`, file, file.name);
    });

    return this.httpClient
      .post<{ success: boolean; data: S3Object[]; MessageError: string }>(`${this.baseUrl}/upload-files/${invoiceID}`, formData)
      .pipe(map(({ data }) => data));
  }

  public deleteFile(idInvoice: string, file: S3Object): Observable<ApiResponse<unknown>> {
    return this.httpClient.post<ApiResponse<unknown>>(`${this.baseUrl}/delete-files/${idInvoice}`, [file]);
  }
}
